import React from "react";
import {
    formatModuleScoreConditions,
    groupModuleScores,
} from "components/ApplicationFilters/FiltersAndChips/ModuleScores/moduleScoreLabels";
import {
    getQuestionResultLabelParts,
    loadQuestionResultLabelData,
} from "components/ApplicationFilters/FiltersAndChips/QuestionResult/questionResultLabels";
import {
    ApplicationFilters,
    ApplicationFiltersStore,
} from "stores/ApplicationFilters/ApplicationFiltersStore";
import { ApplicationFilterItemsStore } from "stores/ApplicationFilters/ApplicationFilterItemsStore";
import { ApplicationFilterValuesStore } from "stores/ApplicationFilters/ApplicationFilterValuesStore";
import { CallDirection } from "stores/ApplicationFilters/Filters/CallDirection";
import { EvaluationType } from "stores/ApplicationFilters/Filters/EvaluationType";
import { HipaaCompliance } from "stores/ApplicationFilters/Filters/HipaaCompliance";
import { MediaType } from "stores/ApplicationFilters/Filters/MediaType";
import { Sentiment } from "stores/ApplicationFilters/Filters/Sentiment";
import {
    SafetyEventTypeOperator,
    SEAckToString,
    SEIdToString,
    SETypeToString,
    resolveSafetyEventTypeOperator,
} from "stores/ApplicationFilters/Filters/SafetyEvent";
import RBCFilterStore from "../DeepFilter/RBCFilterStore";
import { CombinatorRuleType } from "models/RuleCombinator";
import type { HierarchyPath } from "./SearchSubscriptionDialog";
import {
    sortFilters,
    MAX_SHOWN_ITEMS,
    filterDisplayTextByKey,
} from "./helpers";
import { OrganizationService } from "services/OrganizationService";
import { renderToStaticMarkup } from "react-dom/server";
import { formatSegmentHeading } from "components/UI/AcxTranscription/utils";

const organizationService = new OrganizationService();

export const getActiveFilters = (
    filters: ApplicationFilters,
    applicationFiltersStore: ApplicationFiltersStore,
) => {
    if (!filters) {
        return [];
    }
    const extendedMetadataGroupedEntries = Object.keys(
        filters.extendedMetadataGrouped || {},
    );
    const extendedMetadataGroupedKeys = new Set(extendedMetadataGroupedEntries);
    const extendedMetadataEntries = Object.keys(
        filters.extendedMetadata || {},
    ).filter((key) => !extendedMetadataGroupedKeys.has(key));
    const filtersKeys = sortFilters([
        ...Object.keys(filters),
        ...extendedMetadataEntries,
        ...extendedMetadataGroupedEntries,
    ]);
    let activeFilters = filtersKeys.filter((filter) => {
        // is a standard or core ai filter
        if (filterDisplayTextByKey.hasOwnProperty(filter)) {
            return (
                applicationFiltersStore.isDefaultFilterValue(
                    filter,
                    filters,
                ) === false
            );
        }
        if (extendedMetadataGroupedKeys.has(filter)) {
            const groupedItem = filters.extendedMetadataGrouped?.[filter];
            return !!groupedItem && groupedItem.values.length > 0;
        }
        return !!filters.extendedMetadata?.[filter];
    });
    return activeFilters;
};

export const getHierarchyPaths = async (
    hierarchyIds: string[],
    fetchedHierarchyPaths?: HierarchyPath[],
) => {
    const hierarchiesToFetch = hierarchyIds.filter((hi) => {
        return !fetchedHierarchyPaths?.find((hp) => hp.id === hi);
    });

    if (
        !hierarchiesToFetch ||
        hierarchiesToFetch.length === 0 ||
        hierarchiesToFetch.length > MAX_SHOWN_ITEMS
    ) {
        return [];
    }

    const paths = await Promise.all(
        hierarchiesToFetch.map(async (hierarchyId) => {
            const levels =
                await organizationService.getInteractionHierarchyLevels(
                    hierarchyId,
                );
            return {
                id: hierarchyId,
                path: levels.map((level) => level.name).join(" > "),
            };
        }),
    );

    return paths;
};

export const generateFilterHtml = async (props: GenerateFiltersParams) => {
    const { filterJsx } = await generateFilters(props);
    const filterHtml = filterJsx
        .map((filter) => renderToStaticMarkup(filter))
        .join("");

    return filterHtml;
};
interface GenerateFiltersParams {
    filters: ApplicationFilters;
    fetchedHierarchyPaths?: HierarchyPath[];
    applicationFiltersStore: ApplicationFiltersStore;
    applicationFilterValuesStore: ApplicationFilterValuesStore;
}

export const generateFilters = async ({
    filters,
    fetchedHierarchyPaths,
    applicationFiltersStore,
    applicationFilterValuesStore,
}: GenerateFiltersParams) => {
    const activeFilters = getActiveFilters(filters, applicationFiltersStore);
    const newHierarchyPaths = await getHierarchyPaths(
        filters?.hierarchyIds || [],
        fetchedHierarchyPaths,
    );
    // Question result labels come from lazily cached module details, which are
    // not guaranteed to be warm when a saved subscription is previewed.
    await loadQuestionResultLabelData(
        filters?.questionResults,
        applicationFilterValuesStore,
    );
    const hierarchyPaths = [
        ...newHierarchyPaths,
        ...(fetchedHierarchyPaths || []),
    ].filter((path) => filters?.hierarchyIds?.includes(path.id));

    const filterJsx = activeFilters
        .map((filter) => {
            const filterItem = subscriptionRenderedFilter({
                key: filter,
                filters,
                applicationFiltersStore,
                applicationFilterValuesStore,
                rbcFilterStore: applicationFiltersStore.rbcFilterStore,
                hierarchyPaths: hierarchyPaths,
            });
            return <React.Fragment key={filter}>{filterItem}</React.Fragment>;
        })
        .filter((f) => f !== null);
    return { filterJsx, newHierarchyPaths };
};

export const subscriptionRenderedFilter = ({
    key,
    filters,
    applicationFiltersStore,
    applicationFilterValuesStore,
    rbcFilterStore,
    hierarchyPaths,
}: {
    key: string;
    filters: ApplicationFilters;
    applicationFiltersStore: ApplicationFiltersStore;
    applicationFilterValuesStore: ApplicationFilterValuesStore;
    rbcFilterStore: RBCFilterStore;
    hierarchyPaths: HierarchyPath[];
}): React.ReactElement | null => {
    switch (key) {
        case "agentIds":
            const agents = applicationFilterValuesStore.agents;
            if (!filters.agentIds || filters.agentIds.length === 0) return null;
            if (filters.agentIds.length <= MAX_SHOWN_ITEMS) {
                return (
                    <li>
                        {"Agents: " +
                            filters.agentIds
                                .map(
                                    (id) =>
                                        agents.find((agent) => agent.id === id)
                                            ?.fullName || id,
                                )
                                .join(", ")}
                    </li>
                );
            } else {
                return (
                    <li>
                        {"Agents: " + filters.agentIds.length + " selected"}
                    </li>
                );
            }
        case "agentManagerIds":
            const agentManagers = applicationFilterValuesStore.agentManagers;
            if (
                !filters.agentManagerIds ||
                filters.agentManagerIds.length === 0
            )
                return null;
            if (filters.agentManagerIds.length <= MAX_SHOWN_ITEMS) {
                return (
                    <li>
                        {"Agent Managers: " +
                            filters.agentManagerIds
                                .map((id) => {
                                    const manager = agentManagers.find(
                                        (candidate) => candidate.id === id,
                                    );
                                    return (
                                        manager?.fullName?.trim() ||
                                        id
                                    );
                                })
                                .join(", ")}
                    </li>
                );
            } else {
                return (
                    <li>
                        {"Agent Managers: " +
                            filters.agentManagerIds.length +
                            " selected"}
                    </li>
                );
            }
        case "beginningSentiment":
            return (
                <li>
                    {"Starting Sentiment: " +
                        Sentiment.toString(filters.beginningSentiment!)}
                </li>
            );
        case "callDirections":
            return (
                <li>
                    {"Call Direction: " +
                        filters.callDirections
                            .map((cd) => CallDirection.toString(cd))
                            .join(", ")}
                </li>
            );
        case "clientCallId":
            return <li>{"Client Call ID: " + filters.clientCallId}</li>;
        case "contactTypes":
            return (
                <li>
                    {"Contact Type: " +
                        filters.contactTypes
                            ?.map((ct) =>
                                applicationFilterValuesStore.getContactTypeLabel(
                                    ct,
                                ),
                            )
                            ?.join(", ")}
                </li>
            );
        case "dataLabels":
            return (
                <li>
                    {"Conversation Labeling: " +
                        (filters.containsDataLabels ? "" : "Excludes") +
                        " " +
                        filters.dataLabels?.map((dl) => dl.name).join(", ")}
                </li>
            );
        case "dateReferenceOption":
            return <li>{"Date Type: " + filters.dateReferenceOption}</li>;
        case "eddyEffectStatus":
            return (
                <li>
                    {"Eddy Effect Signal: " +
                        (filters.eddyEffectStatus
                            ? "Is Present"
                            : "Is Not Present")}
                </li>
            );
        case "embeddedTranscriptionQuery":
            return (
                <li>
                    {" AI Powered Search: " +
                        filters.embeddedTranscriptionQuery}
                </li>
            );
        case "endingSentiment":
            return (
                <li>
                    {"Ending Sentiment: " +
                        Sentiment.toString(filters.endingSentiment!)}
                </li>
            );
        case "evaluationTypes":
            return (
                <li>
                    {"Evaluation Type: " +
                        filters.evaluationTypes
                            ?.map((et) => EvaluationType.toString(et))
                            .join(", ")}
                </li>
            );
        case "favoriteConversations":
            return <li>{"Favorite Conversations Only"}</li>;
        case "hierarchyIds":
            if (!filters.hierarchyIds || filters.hierarchyIds.length === 0)
                return null;
            if (filters.hierarchyIds.length <= MAX_SHOWN_ITEMS) {
                return (
                    <>
                        {hierarchyPaths.map((hp) => {
                            return <li key={hp.id}>{"Hierarchy: " + hp.path}</li>;
                        })}
                    </>
                );
            } else {
                return (
                    <li>
                        {"Hierarchies: " +
                            filters.hierarchyIds.length +
                            " selected"}
                    </li>
                );
            }
        case "hipaaCompliance":
            if (filters.hipaaCompliance === undefined) return null;
            return (
                <li>
                    {"HIPAA Compliance: " +
                        HipaaCompliance.toString(filters.hipaaCompliance)}
                </li>
            );
        case "languages":
            return (
                <li>
                    {"Language: " +
                        filters.languages
                            ?.map(
                                (lang) =>
                                    applicationFiltersStore.languageOptions.find(
                                        (lo) => lo.value === lang.languageValue,
                                    )?.label,
                            )
                            .join(", ")}
                </li>
            );
        case "maxCallDuration":
            const minDuration = filters.minCallDuration ?? 0;
            const maxDuration = filters.maxCallDuration ?? 0;
            return (
                <li>
                    {maxDuration > 0
                        ? `Call Duration: ${minDuration} to ${maxDuration} minutes`
                        : `Call Duration: ${minDuration}+ minutes`}
                </li>
            );
        case "maxMessageCount":
            const minCount = filters.minMessageCount ?? 0;
            const maxCount = filters.maxMessageCount ?? 0;
            return (
                <li>
                    {maxCount > 0
                        ? `Message Count: ${minCount} to ${maxCount}`
                        : `Message Count: ${minCount}+`}
                </li>
            );
        case "mediaTypes":
            return (
                <li>
                    {"Media Type: " +
                        filters.mediaTypes
                            ?.map((mt) => MediaType.toString(mt))
                            .join(", ")}
                </li>
            );
        case "minCallDuration":
            return null; // Handled in maxCallDuration
        case "minMessageCount":
            return null; // Handled in maxMessageCount
        case "modelResults":
            if (!filters.modelResults || filters.modelResults.length === 0)
                return null;
            if (filters.modelResults.length <= MAX_SHOWN_ITEMS) {
                return (
                    <li>
                        Model Results:
                        <ul>
                            {filters.modelResults.map((mr) => {
                                let results = "";
                                if (mr.results.length <= MAX_SHOWN_ITEMS) {
                                    results = mr.results.join(", ");
                                } else {
                                    results = mr.results.length + " selected";
                                }
                                return (
                                    <li key={mr.name}>
                                        {mr.name} - {results}
                                    </li>
                                );
                            })}
                        </ul>
                    </li>
                );
            } else {
                return (
                    <li>
                        {"Model Results: " +
                            filters.modelResults.length +
                            " selected"}
                    </li>
                );
            }
        case "moduleScores": {
            if (!filters.moduleScores || filters.moduleScores.length === 0)
                return null;
            // Counted by module, because that is what one line summarizes: a module with three
            // conditions is one entry here, not three.
            const moduleScoreGroups = groupModuleScores(filters.moduleScores);
            if (moduleScoreGroups.length <= MAX_SHOWN_ITEMS) {
                const hierarchyLicensedModules =
                    applicationFilterValuesStore.hierarchyLicensedModules;
                return (
                    <li>
                        Module Scores:
                        <ul>
                            {moduleScoreGroups.map(
                                (group) => {
                                    const title =
                                        hierarchyLicensedModules?.find(
                                            (hlm) =>
                                                hlm.id ===
                                                group.licensedModuleId,
                                        )?.title;
                                    return (
                                        <li key={group.licensedModuleId}>
                                            {title}:{" "}
                                            {formatModuleScoreConditions(
                                                group.conditions.map(
                                                    (condition) =>
                                                        condition.entry,
                                                ),
                                            )}
                                        </li>
                                    );
                                },
                            )}
                        </ul>
                    </li>
                );
            } else {
                return (
                    <li>
                        {"Module Scores: " +
                            moduleScoreGroups.length +
                            " selected"}
                    </li>
                );
            }
        }
        case "questionResults": {
            const appliedQuestionResults = (filters.questionResults ?? []).filter(
                (qr) => qr.tagIds.length > 0,
            );
            if (appliedQuestionResults.length === 0) return null;
            if (appliedQuestionResults.length <= MAX_SHOWN_ITEMS) {
                return (
                    <li>
                        Question Results:
                        <ul>
                            {appliedQuestionResults.map((qr) => {
                                const { moduleLabel, questionLabel, tagLabels } =
                                    getQuestionResultLabelParts(
                                        qr,
                                        applicationFilterValuesStore,
                                    );
                                const results =
                                    tagLabels.length <= MAX_SHOWN_ITEMS
                                        ? tagLabels.join(", ")
                                        : tagLabels.length + " selected";
                                return (
                                    <li
                                        key={`${qr.licensedModuleId}::${qr.questionId}`}
                                    >
                                        {moduleLabel} - {questionLabel}:{" "}
                                        {results}
                                    </li>
                                );
                            })}
                        </ul>
                    </li>
                );
            } else {
                return (
                    <li>
                        {"Question Results: " +
                            appliedQuestionResults.length +
                            " selected"}
                    </li>
                );
            }
        }
        case "patientJourneyStages":
            if (
                !filters.patientJourneyStages ||
                filters.patientJourneyStages.length === 0
            )
                return null;
            if (filters.patientJourneyStages.length <= MAX_SHOWN_ITEMS) {
                const options = applicationFiltersStore.journeyStageOptions;
                return (
                    <li>
                        {"Patient Journey: " +
                            filters.patientJourneyStages
                                .map((pjs) => {
                                    return options.find(
                                        (opt) => opt.value === pjs,
                                    )?.label;
                                })
                                .join(", ")}
                    </li>
                );
            } else {
                return (
                    <li>
                        {"Patient Journey Stages: " +
                            filters.patientJourneyStages.length +
                            " selected"}
                    </li>
                );
            }
        case "primaryTopics":
            if (!filters.primaryTopics || filters.primaryTopics.length === 0)
                return null;
            if (filters.primaryTopics.length <= MAX_SHOWN_ITEMS) {
                return (
                    <li>
                        {`Primary Topic: ${
                            filters.containsPrimaryTopics ? "" : "Excludes "
                        }` +
                            filters.primaryTopics
                                .map((pt) => pt.topicLabel)
                                .join(", ")}
                    </li>
                );
            } else {
                return (
                    <li>
                        {`Primary Topics: ` +
                            filters.primaryTopics.length +
                            " selected"}
                    </li>
                );
            }
        case "drivers":
            if (!filters.drivers || filters.drivers.length === 0) return null;
            if (filters.drivers.length <= MAX_SHOWN_ITEMS) {
                return (
                    <li>
                        {`Primary Driver: ${
                            filters.containsDrivers ? "" : "Excludes "
                        }` +
                            filters.drivers
                                .map((driver) => driver.driverName)
                                .join(", ")}
                    </li>
                );
            } else {
                return (
                    <li>
                        {`Primary Drivers: ` +
                            filters.drivers.length +
                            " selected"}
                    </li>
                );
            }
        case "rbcFilterItems":
            const rbcFilterItems = filters.rbcFilterItems;
            if (!rbcFilterItems || rbcFilterItems.length === 0) return null;
            const classifierIds = rbcFilterItems.flatMap(
                (item) => item.classifierIds,
            );

            const buildClassifierDisplay = (items: typeof rbcFilterItems) => {
                let display = "";
                items.forEach((item, index) => {
                    if (index > 0) {
                        display +=
                            item.combinator === CombinatorRuleType.And
                                ? " and "
                                : " or ";
                    }

                    display += item.value ? " contains " : " excludes ";

                    let list = item.classifierIds
                        .map(
                            (cId) =>
                                rbcFilterStore.classifiersList.find(
                                    (c) => c.id === cId,
                                )?.name,
                        )
                        .join(", ");
                    if (index < items.length - 1 && items[index + 1]) {
                        list += ", ";
                    }

                    display += list;
                });
                return display;
            };

            return (
                <li>
                    Classifiers:{" "}
                    {classifierIds.length <= MAX_SHOWN_ITEMS
                        ? buildClassifierDisplay(rbcFilterItems)
                        : classifierIds.length + " selected"}
                </li>
            );
        case "outlineSegmentFilterItems":
            const outlineSegmentFilterItems =
                filters.outlineSegmentFilterItems?.filter(
                    (item) => item.segments.length > 0,
                ) ?? [];
            if (outlineSegmentFilterItems.length === 0) return null;

            const selectedSegments = outlineSegmentFilterItems.flatMap(
                (item) => item.segments,
            );

            const buildOutlineDisplay = (
                items: typeof outlineSegmentFilterItems,
            ) => {
                let display = "";
                items.forEach((item, index) => {
                    if (index > 0) {
                        const previous = items[index - 1];
                        display +=
                            item.filterGroupId === previous.filterGroupId
                                ? " and "
                                : " or ";
                    }

                    display += item.value ? " contains " : " excludes ";
                    display += item.segments
                        .map((segment) => formatSegmentHeading(segment))
                        .join(", ");
                });
                return display;
            };

            return (
                <li>
                    Outline:{" "}
                    {selectedSegments.length <= MAX_SHOWN_ITEMS
                        ? buildOutlineDisplay(outlineSegmentFilterItems)
                        : selectedSegments.length + " selected"}
                </li>
            );
        case "safetyEvent":
            const safetyEvent = filters.safetyEvent;
            if (!safetyEvent) return null;
            const seTypePhrase =
                resolveSafetyEventTypeOperator(safetyEvent) === SafetyEventTypeOperator.And
                    ? "All"
                    : "Any";
            return (
                <>
                    {safetyEvent.identification !== null && (
                        <li>
                            Safety Event: {SEIdToString(safetyEvent.identification)}
                        </li>
                    )}
                    {safetyEvent.types.length > 0 && (
                        <li>
                            Safety Event Type is{" "}
                            {seTypePhrase +
                                ": " +
                                safetyEvent.types
                                    .map((type) => SETypeToString(type))
                                    .join(", ")}
                        </li>
                    )}
                    {safetyEvent.acknowledgments.length > 0 && (
                        <li>
                            Safety Event Acknowledgement is{" "}
                            {safetyEvent.acknowledgments
                                .map((ack) => SEAckToString(ack))
                                .join(", ")}
                        </li>
                    )}
                </>
            );
        case "topics":
            if (!filters.topics || filters.topics.length === 0) return null;

            // This line is plain text and cannot carry a status tag, so the marker is what tells it
            // apart from the supported Theme filter's line below -- and only for viewers who can see
            // this filter's Deprecated status, since the others have no second Theme filter to
            // confuse it with. See ApplicationFilterItemsStore.withDeprecationMarker.
            const legacyThemeLabel = applicationFiltersStore.rootStore
                .getStore(ApplicationFilterItemsStore)
                .withDeprecationMarker("topics", "Primary Theme");

            if (filters.topics.length <= MAX_SHOWN_ITEMS) {
                return (
                    <li>
                        {`${legacyThemeLabel}: ${
                            filters.containsTopics ? "" : "Excludes "
                        } ` +
                            filters.topics.map((t) => t.topicLabel).join(", ")}
                    </li>
                );
            } else {
                return (
                    <li>
                        {`${legacyThemeLabel}: ` +
                            filters.topics.length +
                            " selected"}
                    </li>
                );
            }
        case "themes":
            if (!filters.themes || filters.themes.length === 0) return null;

            if (filters.themes.length <= MAX_SHOWN_ITEMS) {
                return (
                    <li>
                        {`Primary Theme: ${
                            filters.containsThemes ? "" : "Excludes "
                        } ` +
                            filters.themes.map((t) => t.themeLabel).join(", ")}
                    </li>
                );
            } else {
                return (
                    <li>
                        {`Primary Theme: ` +
                            filters.themes.length +
                            " selected"}
                    </li>
                );
            }
        case "wordsAndPhrasesItems":
            if (!filters.wordsAndPhrasesItems) return null;
            if (!filters.wordsAndPhrasesIsInputStandard)
                return <li>Words or Phrases: Advanced</li>;
            const wordsAndPhrasesItems = filters.wordsAndPhrasesItems;
            const wordsAndPhrasesContainsItems = wordsAndPhrasesItems.filter(
                (wpi) => wpi.value === "Contains",
            );
            const wordsAndPhrasesNotContainsItems = wordsAndPhrasesItems.filter(
                (wpi) => wpi.value === "Does not contain",
            );
            const wordsAndPhrasesStartsWithItems = wordsAndPhrasesItems.filter(
                (wpi) => wpi.value === "Starts with",
            );

            const buildWordsAndPhrasesDisplay = (
                items: typeof wordsAndPhrasesItems,
            ) => {
                if (items.length <= MAX_SHOWN_ITEMS) {
                    let display = "";
                    items.forEach((item, index) => {
                        if (index > 0) {
                            display +=
                                item.combinator === CombinatorRuleType.And
                                    ? " and "
                                    : " or ";
                        }
                        display += `"${item.searchPhrase}"`;
                    });
                    return display;
                } else {
                    return items.length + " selected";
                }
            };

            return (
                <>
                    {wordsAndPhrasesContainsItems.length > 0 && (
                        <li>
                            {"Words or Phrases: " +
                                buildWordsAndPhrasesDisplay(
                                    wordsAndPhrasesContainsItems,
                                )}
                        </li>
                    )}
                    {wordsAndPhrasesNotContainsItems.length > 0 && (
                        <li>
                            {"Words or Phrases: excludes " +
                                buildWordsAndPhrasesDisplay(
                                    wordsAndPhrasesNotContainsItems,
                                )}
                        </li>
                    )}
                    {wordsAndPhrasesStartsWithItems.length > 0 && (
                        <li>
                            {"Words or Phrases: starts with " +
                                buildWordsAndPhrasesDisplay(
                                    wordsAndPhrasesStartsWithItems,
                                )}
                        </li>
                    )}
                </>
            );

        default:
            const extendedMetadataGroupedItem =
                filters.extendedMetadataGrouped?.[key];
            if (
                extendedMetadataGroupedItem &&
                extendedMetadataGroupedItem.values.length > 0
            ) {
                const label = extendedMetadataGroupedItem.contains
                    ? ""
                    : "excludes ";
                const values =
                    extendedMetadataGroupedItem.values.length <= MAX_SHOWN_ITEMS
                        ? extendedMetadataGroupedItem.values.join(", ")
                        : `${extendedMetadataGroupedItem.values.length} selected`;
                return (
                    <li>
                        {key}: {label} {values}
                    </li>
                );
            }
            if (filters.extendedMetadata?.[key]) {
                return (
                    <li>
                        {key}: {filters.extendedMetadata?.[key]}
                    </li>
                );
            }
            return null;
    }
};
